import React from 'react';
import { useParams, Link } from 'react-router-dom';
import DestinationDetailHero from '../components/Destinations/DestinationDetailHero';
import DestinationOverview from '../components/Destinations/DestinationOverview';
import DestinationHighlights from '../components/Destinations/DestinationHighlights';
import DestinationWildlife from '../components/Destinations/DestinationWildlife';
import DestinationActivities from '../components/Destinations/DestinationActivities';
import DestinationBestTime from '../components/Destinations/DestinationBestTime';
import DestinationGallery from '../components/Destinations/DestinationGallery';
import DestinationTours from '../components/Destinations/DestinationTours';
import './DestinationDetailPage.css';

function DestinationDetailPage() {
  const { slug } = useParams();

  // Destination data - in a real app, this would come from an API or data file
  const destinationsData = {
    'arusha-national-park': {
      id: 1,
      name: 'Arusha National Park',
      image: '/assets/imgs/destination/arusha-national-park.jpg',
      category: 'National Park',
      description: 'A diverse ecosystem with lush forests, wildlife, and stunning landscapes.',
      fullDescription: 'Arusha National Park is a gem of Tanzania, offering a unique combination of diverse ecosystems within a compact area. From the lush montane forests of Mount Meru to the alkaline Momella Lakes, this park provides an incredible variety of landscapes and wildlife viewing opportunities. The park\'s proximity to Arusha town makes it easily accessible, perfect for day trips or short stays.',
      whyVisit: 'Arusha National Park is perfect for travelers seeking diverse ecosystems in a compact area. It\'s ideal for first-time safari visitors, bird enthusiasts, and those looking to combine wildlife viewing with mountain climbing. The park offers excellent value and can be easily combined with other northern circuit destinations.',
      highlights: [
        'Diverse wildlife including giraffes and colobus monkeys',
        'Stunning Mount Meru backdrop',
        'Varied landscapes from rainforest to alpine desert',
        'Momella Lakes with flamingos',
        'Excellent bird watching opportunities'
      ],
      bestTime: 'Year-round',
      duration: '1-2 days',
      location: 'Arusha Region, Tanzania',
      size: '137 km²',
      established: '1960',
      wildlife: ['Giraffes', 'Colobus Monkeys', 'Buffalo', 'Elephants', 'Flamingos', 'Over 400 bird species'],
      activities: ['Game Drives', 'Walking Safaris', 'Bird Watching', 'Mountain Climbing', 'Canoeing'],
      images: [
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/ndutu.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/serengeti1.jpg'
      ]
    },
    'serengeti-national-park': {
      id: 5,
      name: 'Serengeti National Park',
      image: '/assets/imgs/destination/serengeti1.jpg',
      category: 'National Park',
      description: 'Iconic safari destination with endless plains and abundant wildlife.',
      fullDescription: 'The Serengeti National Park is one of the most famous wildlife sanctuaries in the world. Spanning over 14,750 square kilometers, it hosts the largest terrestrial mammal migration in the world, making it one of the Seven Natural Wonders of Africa. The endless plains, dramatic river crossings, and incredible predator-prey interactions create an unparalleled safari experience that draws visitors from around the globe.',
      whyVisit: 'The Serengeti offers the quintessential African safari experience. Witness the great migration, see the Big Five, and experience the raw beauty of untouched wilderness. It\'s a must-visit for wildlife photographers, nature enthusiasts, and anyone seeking an authentic African adventure. The park\'s vastness ensures you\'ll always find solitude and incredible wildlife encounters.',
      highlights: [
        'Endless savannah landscapes',
        'Home to the great migration',
        'Incredible predator populations',
        'UNESCO World Heritage Site',
        'Big Five viewing opportunities'
      ],
      bestTime: 'Year-round',
      duration: '3-7 days',
      location: 'Northern Tanzania',
      size: '14,750 km²',
      established: '1951',
      wildlife: ['Lions', 'Leopards', 'Cheetahs', 'Elephants', 'Rhinos', 'Buffalo', 'Wildebeest', 'Zebras'],
      activities: ['Game Drives', 'Hot Air Balloon Safaris', 'Walking Safaris', 'Bird Watching', 'Photography'],
      images: [
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/ndutu.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'ngorongoro-crater': {
      id: 4,
      name: 'Ngorongoro Crater',
      image: '/assets/imgs/destination/ngorongoro1.jpg',
      category: 'Conservation Area',
      description: 'A UNESCO World Heritage site with incredible wildlife density.',
      fullDescription: 'The Ngorongoro Crater is the world\'s largest intact volcanic caldera, forming a spectacular bowl of about 265 square kilometers. This natural wonder is home to an extraordinary concentration of wildlife, including the endangered black rhino. The crater floor provides a natural enclosure where animals thrive, creating one of the densest concentrations of wildlife in Africa. The dramatic scenery, from the crater rim to the floor, offers breathtaking views and unparalleled game viewing opportunities.',
      whyVisit: 'Ngorongoro Crater offers the best chance to see the Big Five in a single day. The enclosed nature of the crater means wildlife is concentrated and easier to spot. It\'s perfect for travelers with limited time who want to maximize their wildlife viewing experience. The stunning scenery and high success rate for spotting rare animals make it a photographer\'s paradise.',
      highlights: [
        'World\'s largest intact volcanic caldera',
        'High concentration of wildlife',
        'Home to endangered black rhinos',
        'UNESCO World Heritage Site',
        'Stunning panoramic views'
      ],
      bestTime: 'Year-round',
      duration: '1-2 days',
      location: 'Ngorongoro Conservation Area',
      size: '265 km² (crater floor)',
      established: '1959',
      wildlife: ['Black Rhinos', 'Lions', 'Elephants', 'Buffalo', 'Hippos', 'Flamingos', 'Hyenas'],
      activities: ['Game Drives', 'Crater Descent', 'Maasai Cultural Visits', 'Bird Watching', 'Photography'],
      images: [
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/ngorongoro.webp',
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/manyara.jpg'
      ]
    },
    'zanzibar-island': {
      id: 8,
      name: 'Zanzibar Island',
      image: '/assets/imgs/destination/zanzibar.jpeg',
      category: 'Beach',
      description: 'Tropical paradise with white sandy beaches and rich cultural heritage.',
      fullDescription: 'Zanzibar is a tropical paradise off the coast of Tanzania, known for its pristine white sandy beaches, turquoise waters, and rich Swahili culture. The historic Stone Town is a UNESCO World Heritage Site, showcasing a unique blend of Arab, Persian, Indian, and European influences. Beyond the beaches, Zanzibar offers spice tours, dolphin watching, diving, and a vibrant cultural scene that reflects its position as a historic trading hub.',
      whyVisit: 'Zanzibar is the perfect complement to a Tanzanian safari, offering relaxation and cultural immersion. It\'s ideal for honeymooners, beach lovers, and cultural enthusiasts. The combination of pristine beaches, rich history, and warm hospitality creates an unforgettable island experience. Perfect for extending your Tanzania adventure with some well-deserved relaxation.',
      highlights: [
        'Pristine white sandy beaches',
        'Rich Swahili cultural heritage',
        'Historic Stone Town UNESCO site',
        'Crystal clear turquoise waters',
        'Spice plantations and tours'
      ],
      bestTime: 'Year-round',
      duration: '3-7 days',
      location: 'Zanzibar Archipelago',
      size: '2,461 km²',
      established: 'Historical',
      wildlife: ['Dolphins', 'Sea Turtles', 'Various Marine Life'],
      activities: ['Beach Relaxation', 'Snorkeling', 'Diving', 'Spice Tours', 'Stone Town Tours', 'Dolphin Watching'],
      images: [
        '/assets/imgs/destination/zanzibar.jpeg',
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg'
      ]
    },
    'lake-manyara-national-park': {
      id: 2,
      name: 'Lake Manyara National Park',
      image: '/assets/imgs/destination/lake-manyara.jpg',
      category: 'National Park',
      description: 'Famous for its tree-climbing lions and diverse birdlife.',
      fullDescription: 'Lake Manyara National Park is a small but diverse park located at the base of the Great Rift Valley escarpment. Despite its compact size, it offers an incredible variety of ecosystems, from the alkaline lake to the groundwater forest, making it one of Tanzania\'s most scenic parks. The park is famous for its tree-climbing lions, massive flocks of flamingos, and the dramatic backdrop of the Rift Valley wall.',
      whyVisit: 'Lake Manyara is perfect for a day trip or as part of a northern circuit safari. Its compact size means you can see a lot in a short time, making it ideal for travelers with limited schedules. The unique tree-climbing lions and incredible birdlife make it a must-visit for wildlife enthusiasts. The scenic beauty and diverse ecosystems offer something for everyone.',
      highlights: [
        'Unique tree-climbing lions',
        'Massive flamingo populations',
        'Scenic lake views and diverse ecosystems',
        'Over 400 bird species',
        'Groundwater forest with diverse wildlife'
      ],
      bestTime: 'Year-round',
      duration: '1 day',
      location: 'Manyara Region, Tanzania',
      size: '325 km²',
      established: '1960',
      wildlife: ['Tree-climbing Lions', 'Elephants', 'Hippos', 'Flamingos', 'Baboons', 'Over 400 bird species'],
      activities: ['Game Drives', 'Bird Watching', 'Walking Safaris', 'Photography'],
      images: [
        '/assets/imgs/destination/lake-manyara.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/serengeti1.jpg'
      ]
    },
    'ndutu-plains': {
      id: 3,
      name: 'Ndutu Plains',
      image: '/assets/imgs/destination/ndutu.jpg',
      category: 'Conservation Area',
      description: 'Witness the great wildebeest migration and abundant wildlife.',
      fullDescription: 'The Ndutu Plains are part of the Ngorongoro Conservation Area and serve as a critical calving ground for the great wildebeest migration. This vast open grassland provides excellent wildlife viewing opportunities, especially during the calving season when thousands of wildebeest give birth. The area offers a more intimate and less crowded safari experience compared to the main Serengeti, with excellent predator viewing and the dramatic spectacle of the migration.',
      whyVisit: 'Ndutu Plains offer an authentic wilderness experience away from the crowds. It\'s perfect for witnessing the great migration calving season and offers excellent predator viewing. Wildlife photographers will appreciate the open plains and abundant wildlife. The area provides a more intimate safari experience while still offering incredible wildlife encounters.',
      highlights: [
        'Wildebeest migration hotspot',
        'Vast open plains',
        'Excellent predator viewing',
        'Calving season spectacle',
        'Remote wilderness experience'
      ],
      bestTime: 'December - March',
      duration: '2-3 days',
      location: 'Ngorongoro Conservation Area',
      size: '8,000 km²',
      established: '1959',
      wildlife: ['Wildebeest', 'Zebras', 'Lions', 'Cheetahs', 'Hyenas', 'Gazelles'],
      activities: ['Game Drives', 'Migration Viewing', 'Photography', 'Bird Watching'],
      images: [
        '/assets/imgs/destination/ndutu.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'tarangire-national-park': {
      id: 6,
      name: 'Tarangire National Park',
      image: '/assets/imgs/destination/tarangire1.jpg',
      category: 'National Park',
      description: 'Known for its large elephant herds and baobab trees.',
      fullDescription: 'Tarangire National Park is famous for its massive elephant herds and iconic baobab trees that dot the landscape. During the dry season, the Tarangire River becomes a lifeline, attracting thousands of animals and creating one of Africa\'s greatest concentrations of wildlife. The park\'s diverse habitats, from swamps to savannah, support an incredible variety of species, making it one of Tanzania\'s most underrated but rewarding destinations.',
      whyVisit: 'Tarangire offers some of the best elephant viewing in Tanzania, with herds numbering in the hundreds. The iconic baobab trees create a unique and photogenic landscape. It\'s perfect for bird enthusiasts, with over 550 species recorded. The park provides excellent value and is less crowded than other northern circuit destinations, offering a more intimate safari experience.',
      highlights: [
        'Massive elephant herds',
        'Iconic baobab trees',
        'Seasonal wildlife migrations',
        'Excellent bird watching',
        'Diverse landscapes'
      ],
      bestTime: 'June - October',
      duration: '1-2 days',
      location: 'Manyara Region, Tanzania',
      size: '2,850 km²',
      established: '1970',
      wildlife: ['Elephants', 'Lions', 'Leopards', 'Buffalo', 'Giraffes', 'Over 550 bird species'],
      activities: ['Game Drives', 'Bird Watching', 'Walking Safaris', 'Photography'],
      images: [
        '/assets/imgs/destination/tarangire1.jpg',
        '/assets/imgs/destination/tarangire.jpg',
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/manyara.jpg'
      ]
    },
    'mount-kilimanjaro': {
      id: 7,
      name: 'Mount Kilimanjaro',
      image: '/assets/imgs/destination/Mount-Kilimanjaro.jpg',
      category: 'Mountain',
      description: 'Africa\'s highest peak and one of the world\'s most iconic mountains.',
      fullDescription: 'Mount Kilimanjaro is Africa\'s highest peak at 5,895 meters and one of the Seven Summits. This dormant volcano offers multiple climbing routes through diverse ecosystems, from tropical rainforests to arctic glaciers, making it one of the world\'s most accessible high-altitude climbs. The mountain\'s five distinct climate zones create a unique journey from the tropics to the arctic, culminating in a spectacular sunrise summit view.',
      whyVisit: 'Kilimanjaro offers the adventure of a lifetime, accessible to fit hikers without technical climbing skills. The diverse ecosystems and stunning views make every day of the climb memorable. Reaching the summit at sunrise is an experience that will stay with you forever. It\'s perfect for adventure seekers, hikers, and those looking to challenge themselves while experiencing Africa\'s natural beauty.',
      highlights: [
        'Africa\'s highest peak (5,895m)',
        'Multiple climbing routes',
        'Stunning sunrise views from the summit',
        'Diverse ecosystems from rainforest to glacier',
        'One of the Seven Summits'
      ],
      bestTime: 'January - March, June - October',
      duration: '5-9 days',
      location: 'Kilimanjaro Region, Tanzania',
      size: '1,688 km²',
      established: '1973',
      wildlife: ['Colobus Monkeys', 'Various Bird Species', 'Mountain Wildlife'],
      activities: ['Mountain Climbing', 'Hiking', 'Photography', 'Sunrise Viewing'],
      images: [
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'ruaha-national-park': {
      id: 9,
      name: 'Ruaha National Park',
      image: '/assets/imgs/destination/ruaha.webp',
      category: 'National Park',
      description: 'Tanzania\'s largest national park with diverse wildlife and stunning landscapes.',
      fullDescription: 'Ruaha National Park is Tanzania\'s largest national park, covering over 20,000 square kilometers of pristine wilderness. This remote park offers an authentic safari experience with high concentrations of elephants, large prides of lions, and diverse birdlife. The Great Ruaha River flows through the park, providing a lifeline for wildlife and creating stunning landscapes. The park\'s isolation means fewer visitors and a more exclusive safari experience.',
      whyVisit: 'Ruaha offers an authentic, uncrowded safari experience with excellent wildlife viewing. The park has one of Tanzania\'s highest elephant populations and is known for large lion prides. Its remote location ensures an exclusive experience away from the crowds. Perfect for travelers seeking a true wilderness adventure and excellent game viewing.',
      highlights: [
        'Largest national park in Tanzania',
        'High concentration of elephants',
        'Remote and unspoiled wilderness',
        'Excellent predator viewing',
        'Diverse birdlife'
      ],
      bestTime: 'June - October',
      duration: '3-5 days',
      location: 'Iringa Region, Tanzania',
      size: '20,226 km²',
      established: '1964',
      wildlife: ['Elephants', 'Lions', 'Leopards', 'Cheetahs', 'Wild Dogs', 'Over 570 bird species'],
      activities: ['Game Drives', 'Walking Safaris', 'Bird Watching', 'Photography'],
      images: [
        '/assets/imgs/destination/ruaha.jpeg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'nyerere-national-park': {
      id: 10,
      name: 'Nyerere National Park',
      image: '/assets/imgs/destination/nyerere.jpg',
      category: 'National Park',
      description: 'Formerly Selous Game Reserve, one of Africa\'s largest protected areas.',
      fullDescription: 'Nyerere National Park, formerly part of the Selous Game Reserve, is one of Africa\'s largest protected areas. This UNESCO World Heritage Site offers diverse ecosystems from miombo woodlands to grassy plains and the mighty Rufiji River. The park is renowned for its walking safaris, boat safaris, and excellent wildlife viewing, including large populations of elephants, hippos, and crocodiles.',
      whyVisit: 'Nyerere offers unique safari experiences including walking and boat safaris, setting it apart from other parks. It\'s one of Africa\'s largest protected areas with diverse ecosystems and excellent wildlife viewing. The park is less visited, offering an exclusive experience. Perfect for adventurous travelers seeking varied safari activities.',
      highlights: [
        'One of Africa\'s largest game reserves',
        'Excellent walking safari opportunities',
        'Boat safaris on the Rufiji River',
        'Diverse ecosystems and wildlife',
        'UNESCO World Heritage Site'
      ],
      bestTime: 'June - October',
      duration: '4-6 days',
      location: 'Morogoro Region, Tanzania',
      size: '30,893 km²',
      established: '1922',
      wildlife: ['Elephants', 'Hippos', 'Crocodiles', 'Wild Dogs', 'Lions', 'Over 440 bird species'],
      activities: ['Walking Safaris', 'Boat Safaris', 'Game Drives', 'Bird Watching', 'Fishing'],
      images: [
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ruaha.jpeg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'mikumi-national-park': {
      id: 11,
      name: 'Mikumi National Park',
      image: '/assets/imgs/destination/mikumi.jpg',
      category: 'National Park',
      description: 'Accessible park with abundant wildlife and beautiful savannah landscapes.',
      fullDescription: 'Mikumi National Park is easily accessible from Dar es Salaam, making it a popular choice for shorter safaris. The park features open grasslands, acacia woodlands, and the Mkata River floodplain, which attracts large herds of elephants, buffalo, and various antelope species. Despite its accessibility, Mikumi offers excellent wildlife viewing and is often combined with visits to Udzungwa Mountains.',
      whyVisit: 'Mikumi is perfect for travelers with limited time, being easily accessible from Dar es Salaam. It offers excellent wildlife viewing with large herds of elephants and buffalo. The park provides great value and can be combined with other southern circuit destinations. Ideal for first-time safari visitors or those looking for a shorter safari experience.',
      highlights: [
        'Easy accessibility from Dar es Salaam',
        'Large herds of elephants and buffalo',
        'Open grasslands perfect for game viewing',
        'Great for shorter safaris',
        'Combines well with Udzungwa Mountains'
      ],
      bestTime: 'Year-round',
      duration: '2-3 days',
      location: 'Morogoro Region, Tanzania',
      size: '3,230 km²',
      established: '1964',
      wildlife: ['Elephants', 'Buffalo', 'Lions', 'Giraffes', 'Zebras', 'Wildebeest'],
      activities: ['Game Drives', 'Bird Watching', 'Photography'],
      images: [
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/tarangire1.jpg',
        '/assets/imgs/destination/arusha-national-park.jpg'
      ]
    },
    'mount-meru': {
      id: 12,
      name: 'Mount Meru',
      image: '/assets/imgs/destination/mount-meru.webp',
      category: 'Mountain',
      description: 'Tanzania\'s second highest peak offering challenging climbs and stunning views.',
      fullDescription: 'Mount Meru, at 4,566 meters, is Tanzania\'s second highest peak and offers a challenging but rewarding climb. Located within Arusha National Park, the mountain provides stunning views of Mount Kilimanjaro and the surrounding landscapes. The climb passes through diverse ecosystems from montane forests to alpine deserts, offering excellent wildlife viewing opportunities including colobus monkeys and various bird species.',
      whyVisit: 'Mount Meru is perfect for those seeking a challenging climb with less crowds than Kilimanjaro. It offers stunning views of Kilimanjaro and diverse ecosystems. The climb is shorter and less expensive than Kilimanjaro, making it a great alternative or training climb. Perfect for adventure seekers and those looking to experience Tanzania\'s mountain landscapes.',
      highlights: [
        'Tanzania\'s second highest peak (4,566m)',
        'Less crowded than Kilimanjaro',
        'Stunning views of Kilimanjaro',
        'Diverse ecosystems',
        'Excellent wildlife viewing'
      ],
      bestTime: 'June - October, December - February',
      duration: '3-4 days',
      location: 'Arusha Region, Tanzania',
      size: '137 km²',
      established: '1960',
      wildlife: ['Colobus Monkeys', 'Buffalo', 'Elephants', 'Various Bird Species'],
      activities: ['Mountain Climbing', 'Hiking', 'Wildlife Viewing', 'Photography'],
      images: [
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'mafia-island': {
      id: 13,
      name: 'Mafia Island',
      image: '/assets/imgs/destination/Mafia_Island_Whale_Shark_04.jpg',
      category: 'Beach',
      description: 'Pristine island paradise with world-class diving and marine life.',
      fullDescription: 'Mafia Island is a pristine tropical paradise offering world-class diving, snorkeling, and marine life viewing. The island is surrounded by a marine protected area, ensuring healthy coral reefs and abundant marine biodiversity. With fewer tourists than Zanzibar, Mafia offers a more relaxed and authentic experience, perfect for diving enthusiasts and those seeking tranquility.',
      whyVisit: 'Mafia Island offers world-class diving and snorkeling in pristine waters. The marine protected area ensures excellent marine life viewing. The island is less developed and less crowded than Zanzibar, offering a more authentic experience. Perfect for diving enthusiasts, beach lovers, and those seeking a peaceful island getaway.',
      highlights: [
        'World-class diving and snorkeling',
        'Marine protected area',
        'Pristine beaches and clear waters',
        'Less crowded than Zanzibar',
        'Excellent fishing opportunities'
      ],
      bestTime: 'Year-round',
      duration: '3-5 days',
      location: 'Pwani Region, Tanzania',
      size: '394 km²',
      established: '1995',
      wildlife: ['Dolphins', 'Whale Sharks', 'Sea Turtles', 'Various Marine Species'],
      activities: ['Diving', 'Snorkeling', 'Fishing', 'Beach Relaxation', 'Island Tours'],
      images: [
        '/assets/imgs/destination/zanzibar.jpeg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'pemba-island': {
      id: 14,
      name: 'Pemba Island',
      image: '/assets/imgs/destination/pemba.jpeg',
      category: 'Beach',
      description: 'The "Green Island" known for its lush vegetation and excellent diving.',
      fullDescription: 'Pemba Island, known as the "Green Island" due to its lush vegetation, offers excellent diving, pristine beaches, and authentic Swahili culture. The island is less developed than Zanzibar, providing a more authentic and relaxed experience. The surrounding waters are known for excellent diving with healthy coral reefs and diverse marine life.',
      whyVisit: 'Pemba Island offers excellent diving and snorkeling in pristine waters. The lush green landscapes and authentic Swahili culture provide a unique experience. The island is less developed than Zanzibar, offering tranquility and authenticity. Perfect for diving enthusiasts and those seeking a peaceful island escape.',
      highlights: [
        'Excellent diving and snorkeling',
        'Lush green landscapes',
        'Authentic Swahili culture',
        'Pristine beaches',
        'Less developed than Zanzibar'
      ],
      bestTime: 'Year-round',
      duration: '3-5 days',
      location: 'Zanzibar Archipelago, Tanzania',
      size: '988 km²',
      established: 'N/A',
      wildlife: ['Dolphins', 'Sea Turtles', 'Various Marine Species'],
      activities: ['Diving', 'Snorkeling', 'Beach Relaxation', 'Cultural Tours', 'Spice Tours'],
      images: [
        '/assets/imgs/destination/zanzibar.jpeg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    },
    'gombe-stream-national-park': {
      id: 17,
      name: 'Gombe Stream National Park',
      image: '/assets/imgs/destination/Gombe.jpeg',
      category: 'National Park',
      description: 'Famous for chimpanzee tracking and Jane Goodall\'s research.',
      fullDescription: 'Gombe Stream National Park is famous for Jane Goodall\'s groundbreaking chimpanzee research, which began in 1960. This small but significant park on the shores of Lake Tanganyika offers intimate chimpanzee tracking experiences in dense forest. The park\'s steep valleys, streams, and waterfalls create a beautiful backdrop for wildlife viewing.',
      whyVisit: 'Gombe offers intimate chimpanzee tracking experiences in the footsteps of Jane Goodall. The small park size ensures a personal and immersive experience. The beautiful forest setting and Lake Tanganyika views add to the experience. Perfect for primate enthusiasts and those interested in conservation history.',
      highlights: [
        'Chimpanzee tracking opportunities',
        'Jane Goodall\'s research site',
        'Dense forest and waterfalls',
        'Intimate wildlife experiences',
        'Beautiful Lake Tanganyika views'
      ],
      bestTime: 'May - October',
      duration: '2-3 days',
      location: 'Kigoma Region, Tanzania',
      size: '52 km²',
      established: '1968',
      wildlife: ['Chimpanzees', 'Red Colobus Monkeys', 'Various Bird Species'],
      activities: ['Chimpanzee Tracking', 'Forest Hiking', 'Bird Watching', 'Lake Activities'],
      images: [
        '/assets/imgs/destination/arusha-national-park.jpg',
        '/assets/imgs/destination/serengeti1.jpg',
        '/assets/imgs/destination/ngorongoro1.jpg',
        '/assets/imgs/destination/manyara.jpg',
        '/assets/imgs/destination/tarangire1.jpg'
      ]
    }
  };

  const destination = destinationsData[slug];

  if (!destination) {
    return (
      <div className="destination-not-found">
        <h1>Destination Not Found</h1>
        <p>The destination you're looking for doesn't exist.</p>
        <Link to="/destinations" className="btn">Back to Destinations</Link>
      </div>
    );
  }

  return (
    <div className="destination-detail-page">
      <DestinationDetailHero destination={destination} />
      <DestinationOverview destination={destination} />
      <DestinationHighlights destination={destination} />
      <DestinationWildlife destination={destination} />
      <DestinationActivities destination={destination} />
      <DestinationBestTime destination={destination} />
      <DestinationGallery destination={destination} />
      <DestinationTours destination={destination} />
    </div>
  );
}

export default DestinationDetailPage;

